import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import {
  DollarSign, TrendingUp, TrendingDown, Users, ShoppingBag,
  ArrowUpRight, ArrowDownRight, Loader2, RefreshCw, BarChart3,
  Crown, Repeat, Target, Gift, Mail, Sparkles
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import { SubscriptionFunnelSection } from './SubscriptionFunnelSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '365d', days: 365 },
];

/* ─── Small helper components ─── */
const GrowthBadge = ({ pct }) => {
  if (pct === 0 || pct === undefined) return <span className="text-[10px] text-stone-400">—</span>;
  const up = pct > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-emerald-600' : 'text-rose-500'}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(pct)}%
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, growth, subtext, testId }) => (
  <div data-testid={testId} className="bg-white border border-stone-100 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 rounded-lg bg-stone-50 flex items-center justify-center">
        <Icon className="w-3.5 h-3.5 text-stone-500" />
      </div>
      <span className="text-[11px] font-medium text-stone-400 uppercase tracking-wider">{label}</span>
    </div>
    <div className="flex items-end gap-2">
      <span className="text-xl font-serif font-bold text-stone-800">{value}</span>
      {growth !== undefined && <GrowthBadge pct={growth} />}
    </div>
    {subtext && <p className="text-[10px] text-stone-400 mt-1">{subtext}</p>}
  </div>
);

const BreakdownRow = ({ icon: Icon, label, usd, nok, orders, pct }) => (
  <div className="flex items-center gap-3 py-3 border-b border-stone-50 last:border-0">
    <div className="w-7 h-7 rounded-lg bg-stone-50 flex items-center justify-center flex-shrink-0">
      <Icon className="w-3.5 h-3.5 text-stone-500" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-stone-700">{label}</p>
      <p className="text-[10px] text-stone-400">{orders} orders</p>
    </div>
    <div className="text-right flex-shrink-0">
      {usd > 0 && <p className="text-sm font-semibold text-stone-800">${usd}</p>}
      {nok > 0 && <p className="text-xs text-stone-500">{nok} kr</p>}
      {usd === 0 && nok === 0 && <p className="text-sm text-stone-300">$0</p>}
    </div>
    <div className="w-12 text-right flex-shrink-0">
      <span className="text-[10px] font-medium text-stone-400">{pct}%</span>
    </div>
  </div>
);

/* ─── Custom tooltip ─── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-stone-100 rounded-lg shadow-sm px-3 py-2 text-xs">
      <p className="font-medium text-stone-600 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-stone-500">
          <span className="font-semibold" style={{ color: p.color }}>{p.name}: </span>
          {p.name.includes('NOK') ? `${p.value} kr` : `$${p.value}`}
        </p>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
export const RevenueAnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/revenue-analytics?days=${d}`);
      setData(res.data);
    } catch (err) {
      console.error('Revenue analytics error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(days); }, [days, fetchData]);

  const handlePeriod = (d) => { setDays(d); };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
      </div>
    );
  }

  if (!data) return <p className="text-center text-stone-400 py-10">Failed to load analytics</p>;

  const { revenue, subscription: sub, breakdown, growth, revenue_series: series } = data;
  const totalUsd = revenue.total.usd;
  const totalNok = revenue.total.nok;
  const totalOrders = revenue.orders;

  // Calculate breakdown percentages
  const totalRevForPct = totalUsd || 1;
  const bk = breakdown;
  const pctProd = Math.round((bk.products.usd.revenue / totalRevForPct) * 100);
  const pctSub = Math.round((bk.subscriptions.usd.revenue / totalRevForPct) * 100);
  const pctRec = Math.round((bk.recommendations.usd.revenue / totalRevForPct) * 100);
  const pctRecov = Math.round((bk.recovery.usd.revenue / totalRevForPct) * 100);
  const pctCoup = Math.round((bk.coupons.usd.revenue / totalRevForPct) * 100);

  return (
    <div data-testid="revenue-analytics-dashboard" className="space-y-8 overflow-x-hidden">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-serif font-bold text-stone-800">Revenue & Subscriptions</h2>
        <div className="flex items-center gap-1 bg-stone-50 rounded-lg p-1">
          {PERIODS.map(p => (
            <button
              key={p.days}
              data-testid={`rev-period-${p.days}`}
              onClick={() => handlePeriod(p.days)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${days === p.days ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
            >
              {p.label}
            </button>
          ))}
          <button onClick={() => fetchData(days)} className="ml-1 p-1.5 text-stone-400 hover:text-stone-600">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* KPI grid with growth comparison */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          testId="kpi-revenue"
          icon={DollarSign}
          label="Revenue (USD)"
          value={`$${totalUsd}`}
          growth={growth.revenue_usd.pct}
          subtext={totalNok > 0 ? `+ ${totalNok} kr (NOK)` : `vs prev ${days}d`}
        />
        <KpiCard
          testId="kpi-orders"
          icon={ShoppingBag}
          label="Orders"
          value={totalOrders}
          growth={growth.orders.pct}
          subtext={`vs prev ${days}d`}
        />
        <KpiCard
          testId="kpi-aov"
          icon={BarChart3}
          label="AOV (USD)"
          value={`$${revenue.aov_usd}`}
          growth={growth.aov_usd.pct}
          subtext={`vs prev ${days}d`}
        />
        <KpiCard
          testId="kpi-signups"
          icon={Users}
          label="Signups"
          value={growth.signups.current}
          growth={growth.signups.pct}
          subtext={`vs prev ${days}d`}
        />
      </div>

      {/* Revenue chart */}
      <div className="bg-white border border-stone-100 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-stone-600 mb-4">Revenue over time</h3>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={series} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gradUsd" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1c1917" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#1c1917" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#a8a29e' }} tickFormatter={v => v.slice(5)} />
            <YAxis tick={{ fontSize: 10, fill: '#a8a29e' }} tickFormatter={v => `$${v}`} />
            <Tooltip content={<ChartTooltip />} />
            <Area type="monotone" dataKey="usd" name="USD" stroke="#1c1917" fillOpacity={1} fill="url(#gradUsd)" strokeWidth={2} />
            {totalNok > 0 && (
              <Area type="monotone" dataKey="nok" name="NOK" stroke="#B49A88" fill="none" strokeWidth={1.5} strokeDasharray="4 2" />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Subscription Analytics */}
      <div className="bg-white border border-stone-100 rounded-xl p-5">
        <h3 data-testid="subscription-heading" className="text-sm font-semibold text-stone-600 mb-4">Subscription Analytics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard testId="sub-mrr" icon={Repeat} label="MRR" value={sub.mrr.usd > 0 ? `$${sub.mrr.usd}` : '$0'} subtext={sub.mrr.nok > 0 ? `+ ${sub.mrr.nok} kr` : 'No active paid subs'} />
          <KpiCard testId="sub-arr" icon={TrendingUp} label="ARR" value={sub.arr.usd > 0 ? `$${sub.arr.usd}` : '$0'} subtext="Annualised" />
          <KpiCard testId="sub-active" icon={Crown} label="Active Pro" value={sub.active_pro} subtext={sub.cancelling > 0 ? `${sub.cancelling} cancelling` : 'None cancelling'} />
          <KpiCard testId="sub-churn" icon={TrendingDown} label="Churn rate" value={`${sub.churn_rate}%`} subtext={`${sub.churned} churned this period`} />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <KpiCard testId="sub-new" icon={Sparkles} label="New subs" value={sub.new_subscriptions} />
          <KpiCard testId="sub-churned" icon={ArrowDownRight} label="Churned" value={sub.churned} />
          <KpiCard testId="sub-net-mrr" icon={Target} label="Net MRR Δ" value={sub.net_mrr_growth.usd !== 0 ? `$${sub.net_mrr_growth.usd}` : '$0'} />
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="bg-white border border-stone-100 rounded-xl p-5">
        <h3 data-testid="breakdown-heading" className="text-sm font-semibold text-stone-600 mb-4">Revenue Breakdown</h3>
        <div className="text-[10px] text-stone-400 flex justify-between border-b border-stone-100 pb-2 mb-1 px-10">
          <span>Source</span>
          <span className="ml-auto mr-14">Revenue</span>
          <span>Share</span>
        </div>
        <BreakdownRow icon={ShoppingBag} label="Products" usd={bk.products.usd.revenue} nok={bk.products.nok.revenue} orders={bk.products.usd.count + bk.products.nok.count} pct={pctProd} />
        <BreakdownRow icon={Crown} label="Subscriptions" usd={bk.subscriptions.usd.revenue} nok={bk.subscriptions.nok.revenue} orders={bk.subscriptions.usd.count + bk.subscriptions.nok.count} pct={pctSub} />
        <BreakdownRow icon={Sparkles} label="Recommendation-attributed" usd={bk.recommendations.usd.revenue} nok={bk.recommendations.nok.revenue} orders={bk.recommendations.usd.count + bk.recommendations.nok.count} pct={pctRec} />
        <BreakdownRow icon={Mail} label="Recovery-attributed" usd={bk.recovery.usd.revenue} nok={bk.recovery.nok.revenue} orders={bk.recovery.usd.count + bk.recovery.nok.count} pct={pctRecov} />
        <BreakdownRow icon={Gift} label="Coupon-assisted" usd={bk.coupons.usd.revenue} nok={bk.coupons.nok.revenue} orders={bk.coupons.usd.count + bk.coupons.nok.count} pct={pctCoup} />
      </div>

      {/* Currency note */}
      <p className="text-[10px] text-stone-400 text-center">
        Revenue shown per currency (USD / NOK). No cross-currency conversion applied. Only completed/paid transactions counted.
      </p>

      {/* Subscription Funnel */}
      <div className="mt-10 pt-8 border-t border-stone-100">
        <SubscriptionFunnelSection />
      </div>
    </div>
  );
};
